<!--
  顶部导航
  用途：全站统一导航，展示红色 Logo，切换「首页 / 采购实例 / 联系我们」，并提供多语言切换
  用法：放在 AppLayout 顶部；Logo 仅使用图标，不含 DuoGu 文字
-->
<template>
  <header class="site-header" :class="{ 'is-scrolled': scrolled }">
    <div class="header-inner">
      <router-link class="brand" to="/" :aria-label="t('nav.homeAria')">
        <img class="brand-logo" :src="logoUrl" :alt="t('common.brandLogoAlt')" />
      </router-link>

      <nav class="nav">
        <router-link class="nav-link" to="/">{{ t('common.home') }}</router-link>
        <router-link class="nav-link" to="/cases">{{ t('common.cases') }}</router-link>
        <router-link class="nav-link" to="/contact">{{ t('common.contact') }}</router-link>
      </nav>

      <!-- 多语言切换：中文 / English / Español -->
      <LanguageSwitcher class="header-lang" />

      <router-link class="header-cta" to="/contact">{{ t('common.getQuote') }}</router-link>
    </div>
  </header>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import logoUrl from '@/assets/logo.png'
import LanguageSwitcher from './LanguageSwitcher.vue'
import { updateDocumentTitle } from '@/router'
import { setLocale } from '@/i18n'

const { t, locale } = useI18n()
const route = useRoute()

/** 滚动后加深导航阴影，提升层次感 */
const scrolled = ref(false)

const onScroll = () => {
  scrolled.value = window.scrollY > 8
}

/** 语言变更时同步更新页面标题 */
watch(locale, () => {
  updateDocumentTitle(route)
})

onMounted(() => {
  onScroll()
  window.addEventListener('scroll', onScroll, { passive: true })
  // 确保 html lang 与当前 locale 一致
  setLocale(locale.value)
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<style scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: 100;
  height: var(--header-height);
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid transparent;
  backdrop-filter: blur(10px);
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
}

.site-header.is-scrolled {
  border-bottom-color: var(--color-border);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.06);
}

.header-inner {
  max-width: var(--max-width);
  height: 100%;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.brand {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.brand-logo {
  width: 44px;
  height: 44px;
  object-fit: contain;
  display: block;
}

.nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.nav-link {
  padding: 8px 14px;
  border-radius: 999px;
  color: var(--color-text-secondary);
  font-size: 15px;
  font-weight: 500;
  transition: color 0.2s ease, background 0.2s ease;
}

.nav-link:hover,
/* 仅精确匹配高亮，避免「/」让首页在子路由下也被选中 */
.nav-link.router-link-exact-active {
  color: var(--color-primary);
  background: var(--color-primary-soft);
}

.header-lang {
  flex-shrink: 0;
}

.header-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  border-radius: 999px;
  background: var(--color-primary);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  transition: background 0.2s ease, transform 0.2s ease;
  white-space: nowrap;
}

.header-cta:hover {
  background: var(--color-primary-hover);
  transform: translateY(-1px);
}

@media (max-width: 640px) {
  .header-inner {
    padding: 0 12px;
    gap: 6px;
  }

  .header-cta {
    padding: 8px 10px;
    font-size: 12px;
  }

  .nav-link {
    padding: 8px 6px;
    font-size: 12px;
  }
}
</style>
